import React, { forwardRef } from 'react'
import {
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  useTheme,
  Grid,
  TableFooter,
} from '@mui/material'
import logo from '../../../assets/icons/logo.svg'
import locationIcon from '../../../assets/icons/location.svg'
import mobileIcon from '../../../assets/icons/mobile.svg'
import emailIcon from '../../../assets/icons/Email.svg'
import { getRegistrationNo } from './PatientRegCardPrint'
import { Patient } from 'fhir/r4'
import { getTotalAge, readableDateTime } from '../../../utils/dateUtils'
import { getFilterdData } from './OpCaseSheetPrint'
import { getLocationAddress } from '../utils'
import { useAppSelector } from '../../../app/hooks'
import { selectLocations } from '../../location/locationSlice'
import { emptyString } from '../../Radiology/Constants/constants'
import { profilePicture } from '../constants'
import { allocation } from '../../bedManagement/modals'

type PatientProps = {
  patient: Patient
  diagnosisList: any
  labOrderList: any
  medications: any
  visitsList: any
  cancerscreening?: any
}

export const renderHtmlSafely = (html: string) =>
  html?.split('\n')?.map((line, idx) => (
    <Typography key={idx} variant='body2' component='p' mb={1}>
      {line}
    </Typography>
  ))

const EPrescriptionPrint = ({
  patient,
  visitsList,
  medications,
  labOrderList,
  diagnosisList,
  cancerscreening,
}: PatientProps) => {
  const theme = useTheme()

  const { name, gender, birthDate } = patient
  const { drName, department, date, drRegistrationNo, designation, signature } =
    visitsList

  const registrationNoData: any = getRegistrationNo(patient)
  const registrationNo: any =
    registrationNoData?.regNo?.value ?? registrationNoData?.thopId?.value
  const locations = useAppSelector(selectLocations)
  const locationAddress = getLocationAddress(registrationNo, locations)

  const patientName = `${name?.[0]?.given?.[0]} ${
    name?.[0]?.given?.[1] ?? ''
  } ${name?.[0]?.family}`
  const age = birthDate ? getTotalAge(new Date(birthDate)) : ''
  const DrName = `${drName?.[0]?.given?.[0]} ${drName?.[0]?.given?.[1] ?? ''} ${
    drName?.[0]?.family
  }`
  const appointmentDate = readableDateTime(date)

  const diagnosisData = getFilterdData(diagnosisList)
  const finalDiagnosis = diagnosisData?.ProvisionalDiagnosis ?? ''
  const ListLabOrderList =
    labOrderList?.length > 0 ? labOrderList[0]?.panels : labOrderList
  const cancerscreeningData = cancerscreening?.[0]?.interpretationAi ?? ''

  return (
    <Table>
      <TableBody>
        <TableRow>
          <TableCell>
            <Box>
              <Box
                display='flex'
                justifyContent={'space-between'}
                alignItems='center'
                mb={2}
              >
                <img src={logo} alt='Medunited Logo' width={218} />
                <Box ml={2}>
                  <Typography fontWeight={600} fontSize={14}>
                    {`Dr. ${DrName}`}{' '}
                    <Box component={'span'} fontWeight={400} fontSize={12}>
                      ({designation})
                    </Box>{' '}
                    <Box component={'span'} fontWeight={400} fontSize={10}>
                      Registration No: {drRegistrationNo}
                    </Box>
                  </Typography>
                  <Box display='flex' alignItems='center' mt={0.5}>
                    <Box
                      component={'img'}
                      src={locationIcon}
                      alt='location'
                      width={12}
                      height={12}
                      mr={0.5}
                    />
                    <Typography variant='caption'>
                      {locationAddress?.address?.line?.[0]}
                    </Typography>
                  </Box>
                  <Box display='flex' alignItems='center'>
                    <Box
                      component={'img'}
                      src={mobileIcon}
                      alt='mobile'
                      width={12}
                      height={12}
                      mr={0.5}
                    />

                    <Typography variant='caption'>
                      {locationAddress?.telecom?.[0]?.value}
                    </Typography>
                    <Box
                      component={'img'}
                      src={emailIcon}
                      alt='email'
                      width={12}
                      height={12}
                      mr={0.5}
                      ml={8}
                    />

                    <Typography variant='caption'>
                      {locationAddress?.telecom?.[1]?.value}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Typography
                variant='h4'
                fontSize={'24px'}
                fontWeight={'bold'}
                textAlign={'center'}
                color={'#004d91'}
              >
                e-Prescription
              </Typography>
              <Box border={'1px solid #004d91'} mb={'20px'} />

              <Grid container mt={2} rowSpacing={1.5}>
                <Grid item xs={6}>
                  <Typography>
                    Date of Consultation: &nbsp;{appointmentDate}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>Name: &nbsp;{patientName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>Department: &nbsp;{department}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>Gender: {gender}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>Age: &nbsp;{age}</Typography>
                </Grid>
              </Grid>

              <Typography
                fontWeight={600}
                fontSize={16}
                color={'#004D91'}
                marginTop={theme.spacing(2)}
              >
                Diagnosis
              </Typography>
              <Box border={'1px solid #004d91'} mb={'20px'} />
              {renderHtmlSafely(finalDiagnosis)}

              {cancerscreeningData && (
                <>
                  <Typography
                    fontWeight={600}
                    fontSize={16}
                    color={'#004D91'}
                    marginTop={theme.spacing(2)}
                  >
                    Cancer Screening Interpretations
                  </Typography>
                  <Box border={'1px solid #004d91'} mb={'20px'} />
                  {renderHtmlSafely(cancerscreeningData)}
                </>
              )}

              <Typography
                fontWeight={600}
                fontSize={16}
                color={'#004D91'}
                marginTop={theme.spacing(2)}
              >
                Treatment
              </Typography>
              <Box border={'1px solid #004d91'} mb={'20px'} />

              <Table>
                <TableHead>
                  <TableRow>
                    <Box
                      component={'td'}
                      border={'1px solid #DCDCDC'}
                      padding={theme.spacing(0.5)}
                    >
                      No
                    </Box>
                    <Box
                      component={'td'}
                      border={'1px solid #DCDCDC'}
                      padding={theme.spacing(0.5)}
                    >
                      Medicine
                    </Box>
                    <Box
                      component={'td'}
                      border={'1px solid #DCDCDC'}
                      padding={theme.spacing(0.5)}
                    >
                      Dose Details
                    </Box>
                    <Box
                      component={'td'}
                      border={'1px solid #DCDCDC'}
                      padding={theme.spacing(0.5)}
                    >
                      Instructions
                    </Box>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {medications?.map((e: any, i: number) => (
                    <TableRow key={i}>
                      <Box
                        component={'td'}
                        border={'1px solid #DCDCDC'}
                        padding={theme.spacing(0.5)}
                      >
                        {i + 1}
                      </Box>
                      <Box
                        component={'td'}
                        border={'1px solid #DCDCDC'}
                        padding={theme.spacing(0.5)}
                      >
                        {e?.drug_name ?? ''}
                      </Box>
                      <Box
                        component={'td'}
                        border={'1px solid #DCDCDC'}
                        padding={theme.spacing(0.5)}
                      >
                        {`${e?.dose} ${e?.dose_units} ${e?.frequency}`}
                        {e?.next_visit_date && `, Until: ${e?.next_visit_date}`}
                      </Box>
                      <Box
                        component={'td'}
                        border={'1px solid #DCDCDC'}
                        padding={theme.spacing(0.5)}
                      >
                        {e?.instructions ?? allocation?.hyphen}
                      </Box>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Typography
                fontWeight={600}
                fontSize={16}
                color={'#004D91'}
                marginTop={theme.spacing(2)}
              >
                Special Instructions
              </Typography>
              <Box border={'1px solid #004d91'} mb={'20px'} />

              <Table>
                <TableHead>
                  <TableRow>
                    <Box
                      component={'td'}
                      border={'1px solid #DCDCDC'}
                      padding={theme.spacing(0.5)}
                    >
                      No
                    </Box>
                    <Box
                      component={'td'}
                      border={'1px solid #DCDCDC'}
                      padding={theme.spacing(0.5)}
                    >
                      Lab Test
                    </Box>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ListLabOrderList?.map((panel: any, idx: number) => (
                    <TableRow key={idx}>
                      <Box
                        component={'td'}
                        border={'1px solid #DCDCDC'}
                        padding={theme.spacing(0.5)}
                      >
                        {idx + 1}
                      </Box>
                      <Box
                        component={'td'}
                        border={'1px solid #DCDCDC'}
                        padding={theme.spacing(0.5)}
                      >
                        {panel?.name ?? ''}
                      </Box>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Box
                display={'flex'}
                justifyContent={'flex-end'}
                width={'100%'}
                mt={2}
              >
                <Box display={'flex'} flexDirection={'column'}>
                  {signature !== emptyString && (
                    <img
                      width={80}
                      height={50}
                      src={signature}
                      alt={profilePicture}
                    />
                  )}
                  <em> Signature</em>
                </Box>
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      </TableBody>
      <TableFooter>
        <TableRow>
          <Box component={'td'} p={0}>
            <Box display='flex' width={'100%'}>
              <Box bgcolor={'#F47421'} flex={1}>
                <Typography
                  fontSize={12}
                  color={'#fff'}
                  padding={theme.spacing(0.5)}
                  textAlign={'center'}
                >
                  Note: This prescription is generated on a teleconsultation
                </Typography>
              </Box>
              <Box bgcolor={'#004D91'} flex={1}>
                <Typography
                  fontSize={12}
                  color={'#fff'}
                  padding={theme.spacing(0.5)}
                  textAlign={'center'}
                >
                  A unit of CR Healthcare Services Pvt Ltd (CIN -
                  U85110TG2021PTC153300)
                </Typography>
              </Box>
            </Box>
          </Box>
        </TableRow>
      </TableFooter>
    </Table>
  )
}

const EPrescription = forwardRef<HTMLDivElement, PatientProps>(function Label(
  props,
  ref
) {
  return (
    <div ref={ref}>
      <EPrescriptionPrint {...props} />
    </div>
  )
})

export default EPrescription
